/**
 * Allocation-free drawing primitives built on sprites. Every shape is a
 * sprite (or a few) created once and moved, scaled, and rotated in place, so
 * the frame never allocates. Shapes live in a world container whose scale is
 * pixels per meter with y flipped, so positions are in meters.
 */

import { type Container, Graphics, Rectangle, type Renderer, Sprite, Texture } from "pixi.js";

/** Pixel size of the generated backdrop textures: the sky gradient's rows, and the ridge tiles. */
export const GRADIENT_PX = 256;
/**
 * The ridge tile. Both sides are powers of two on purpose: a tile that is not repeats only once on
 * the GPU and then clamps its last column, which showed in the game as the skyline going flat one
 * tile into a course (the owner, 2026-09-20: "the terrain in the background just becomes a flat
 * line"). The skyline itself is drawn in the lowest RIDGE_BAND_H pixels of the tile, measured up
 * from the bottom, so the tile can grow to the next power of two without moving the mountains.
 */
export const RIDGE_TILE_W = 2048;
export const RIDGE_TILE_H = 512;
export const RIDGE_BAND_H = 320;
export const GLOW_PX = 128;

/** A vertical gradient: color stops at positions 0..1 from the top. */
export type GradientStop = readonly [position: number, color: number];

/** A small deterministic generator for the ridge shapes, so every stage draws the same skyline. */
function lcg(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (Math.imul(s, 1664525) + 1013904223) >>> 0;
    return s / 4294967296;
  };
}

export class ShapeFactory {
  readonly circle: Texture;
  readonly ring: Texture;
  readonly white: Texture = Texture.WHITE;
  /** Diameter in pixels of the circle textures. */
  readonly circlePx = 128;
  private readonly renderer: Renderer;
  private glowTexture: Texture | null = null;
  private ridgeTexture: Texture | null = null;
  private readonly gradients = new Map<string, Texture>();

  constructor(renderer: Renderer) {
    this.renderer = renderer;
    const g = new Graphics();
    g.circle(this.circlePx / 2, this.circlePx / 2, this.circlePx / 2 - 1).fill(0xffffff);
    this.circle = renderer.generateTexture({ target: g, resolution: 1, antialias: true });
    const r = new Graphics();
    r.circle(this.circlePx / 2, this.circlePx / 2, this.circlePx / 2 - 3).stroke({
      width: 4,
      color: 0xffffff,
    });
    this.ring = renderer.generateTexture({ target: r, resolution: 1, antialias: true });
    g.destroy();
    r.destroy();
  }

  /** A soft disc: white at the center fading to nothing at the rim, for glows and halos. Made once. */
  get glow(): Texture {
    if (this.glowTexture) return this.glowTexture;
    const g = new Graphics();
    const c = GLOW_PX / 2;
    const steps = 28;
    for (let i = 0; i < steps; i++) {
      const t = 1 - i / steps;
      g.circle(c, c, (c - 1) * t).fill({ color: 0xffffff, alpha: 0.075 });
    }
    this.glowTexture = this.renderer.generateTexture({ target: g, resolution: 1, antialias: true });
    g.destroy();
    return this.glowTexture;
  }

  /**
   * A one-pixel-wide vertical gradient of GRADIENT_PX rows through `stops` (position 0 is the top).
   * Stretched by a sprite, it is the sky. Made once per distinct set of stops.
   */
  gradient(stops: readonly GradientStop[]): Texture {
    const key = stops.map(([p, c]) => `${p}:${c}`).join(",");
    const cached = this.gradients.get(key);
    if (cached) return cached;
    const g = new Graphics();
    for (let row = 0; row < GRADIENT_PX; row++) {
      const t = row / (GRADIENT_PX - 1);
      let a = stops[0]!;
      let b = stops[stops.length - 1]!;
      for (let i = 0; i + 1 < stops.length; i++) {
        if (t >= stops[i]![0] && t <= stops[i + 1]![0]) {
          a = stops[i]!;
          b = stops[i + 1]!;
          break;
        }
      }
      const span = b[0] - a[0];
      const k = span > 0 ? Math.min(1, Math.max(0, (t - a[0]) / span)) : 0;
      g.rect(0, row, 1, 1).fill(mixColor(a[1], b[1], k));
    }
    const texture = this.renderer.generateTexture({ target: g, resolution: 1, antialias: false });
    g.destroy();
    this.gradients.set(key, texture);
    return texture;
  }

  /** The ridge behind the world: a jagged mountain line, dark, with a faint crest. Tiles horizontally. */
  get ridge(): Texture {
    if (this.ridgeTexture) return this.ridgeTexture;
    this.ridgeTexture = this.makeRidge(0x150434, 0xff2d95, 0.28, 7, true);
    return this.ridgeTexture;
  }

  /**
   * A tile of RIDGE_TILE_W by RIDGE_TILE_H pixels whose skyline (mountains when `peaks`, city
   * blocks otherwise) rises out of the bottom RIDGE_BAND_H pixels, filled down to the bottom in
   * `fill`, with a two-pixel `edge` along the top, drawn from a fixed seed so it is the same on
   * every stage. Heights are fractions of the band, so the tile repeats seamlessly across a
   * viewport and the mountains sit the same distance above the tile's bottom edge.
   */
  private makeRidge(fill: number, edge: number, edgeAlpha: number, seed: number, peaks: boolean): Texture {
    const W = RIDGE_TILE_W;
    const H = RIDGE_TILE_H;
    const band = RIDGE_BAND_H;
    const rnd = lcg(seed);
    const g = new Graphics();
    const xs: number[] = [];
    const ys: number[] = [];
    if (peaks) {
      // A random walk of peaks and valleys that closes on itself, so the tile repeats without a seam.
      const n = 24;
      const heights: number[] = [];
      for (let i = 0; i < n; i++) heights.push(0.35 + rnd() * 0.5);
      heights[n - 1] = heights[0]!;
      for (let i = 0; i < n; i++) {
        const x0 = (i / (n - 1)) * W;
        const x1 = ((i + 1) / (n - 1)) * W;
        const top = H - band * heights[i]!;
        xs.push(x0, Math.min(W, x0 + (x1 - x0) * (0.35 + rnd() * 0.3)));
        ys.push(top, H - band * Math.min(heights[i]!, heights[(i + 1) % n]!) * (0.55 + rnd() * 0.25));
      }
      xs.push(W);
      ys.push(H - band * heights[0]!);
    } else {
      // Blocks of a skyline, one block after another until the tile is full, then a level roof back to the start.
      let x = 0;
      const first = 0.25 + rnd() * 0.3;
      let h = first;
      while (x < W) {
        const w = 40 + rnd() * 90;
        const top = H - band * h;
        xs.push(x, Math.min(W, x + w));
        ys.push(top, top);
        x += w;
        h = 0.18 + rnd() * 0.5;
      }
      ys[ys.length - 1] = H - band * first;
    }
    g.moveTo(0, H);
    for (let i = 0; i < xs.length; i++) g.lineTo(xs[i]!, ys[i]!);
    g.lineTo(W, H);
    g.closePath();
    g.fill(fill);
    g.moveTo(xs[0]!, ys[0]!);
    for (let i = 1; i < xs.length; i++) g.lineTo(xs[i]!, ys[i]!);
    g.stroke({ color: edge, width: 2, alpha: edgeAlpha });
    const texture = this.renderer.generateTexture({
      target: g,
      resolution: 1,
      antialias: true,
      frame: new Rectangle(0, 0, W, H),
    });
    texture.source.addressMode = "repeat";
    g.destroy();
    return texture;
  }

  /** Axis-aligned rectangle, anchored at its bottom-left, in world units. */
  rect(parent: Container, x: number, y: number, w: number, h: number, color: number, alpha = 1): Sprite {
    const s = new Sprite(this.white);
    s.anchor.set(0, 0);
    s.position.set(x, y);
    s.width = w;
    s.height = h;
    s.tint = color;
    s.alpha = alpha;
    parent.addChild(s);
    return s;
  }

  /** A line segment drawn as a rotated thin rectangle. Use setLine() to update. */
  line(parent: Container, color: number, thickness: number, alpha = 1): Sprite {
    const s = new Sprite(this.white);
    s.anchor.set(0, 0.5);
    s.height = thickness;
    s.width = 1;
    s.tint = color;
    s.alpha = alpha;
    parent.addChild(s);
    return s;
  }

  /** Filled disc centered at (x, y) with radius r (world units). */
  disc(parent: Container, x: number, y: number, r: number, color: number, alpha = 1): Sprite {
    const s = new Sprite(this.circle);
    s.anchor.set(0.5);
    s.position.set(x, y);
    s.width = r * 2;
    s.height = r * 2;
    s.tint = color;
    s.alpha = alpha;
    parent.addChild(s);
    return s;
  }

  /** A soft halo centered at (x, y) with radius r (the glow texture, tinted). */
  halo(parent: Container, x: number, y: number, r: number, color: number, alpha = 1): Sprite {
    const s = new Sprite(this.glow);
    s.anchor.set(0.5);
    s.position.set(x, y);
    s.width = r * 2;
    s.height = r * 2;
    s.tint = color;
    s.alpha = alpha;
    parent.addChild(s);
    return s;
  }

  /** Ring (circle outline) centered at (x, y) with radius r. */
  ringAt(parent: Container, x: number, y: number, r: number, color: number, alpha = 1): Sprite {
    const s = new Sprite(this.ring);
    s.anchor.set(0.5);
    s.position.set(x, y);
    s.width = r * 2;
    s.height = r * 2;
    s.tint = color;
    s.alpha = alpha;
    parent.addChild(s);
    return s;
  }
}

/** Point a line sprite from (x0, y0) to (x1, y1). */
export function setLine(s: Sprite, x0: number, y0: number, x1: number, y1: number, thickness?: number): void {
  const dx = x1 - x0;
  const dy = y1 - y0;
  const len = Math.sqrt(dx * dx + dy * dy);
  s.position.set(x0, y0);
  s.rotation = Math.atan2(dy, dx);
  s.width = len;
  if (thickness !== undefined) s.height = thickness;
}

export function setDisc(s: Sprite, x: number, y: number, r: number): void {
  s.position.set(x, y);
  s.width = r * 2;
  s.height = r * 2;
}

/**
 * A capsule made of a rectangle and two discs: a vertical capsule from
 * (x, y0) to (x, y1) with radius r. Use setCapsule() to update.
 */
export interface CapsuleShape {
  body: Sprite;
  top: Sprite;
  bottom: Sprite;
}

export function makeCapsule(f: ShapeFactory, parent: Container, color: number, alpha = 1): CapsuleShape {
  const body = f.rect(parent, 0, 0, 1, 1, color, alpha);
  const top = f.disc(parent, 0, 0, 1, color, alpha);
  const bottom = f.disc(parent, 0, 0, 1, color, alpha);
  return { body, top, bottom };
}

/** Position a capsule between two points with radius r (segment capsule, any orientation). */
export function setCapsule(c: CapsuleShape, x0: number, y0: number, x1: number, y1: number, r: number): void {
  const dx = x1 - x0;
  const dy = y1 - y0;
  const len = Math.sqrt(dx * dx + dy * dy);
  c.body.anchor.set(0, 0.5);
  c.body.position.set(x0, y0);
  c.body.rotation = Math.atan2(dy, dx);
  c.body.width = len;
  c.body.height = r * 2;
  setDisc(c.top, x1, y1, r);
  setDisc(c.bottom, x0, y0, r);
}

export function setCapsuleVisible(c: CapsuleShape, v: boolean): void {
  c.body.visible = v;
  c.top.visible = v;
  c.bottom.visible = v;
}

export function setCapsuleTint(c: CapsuleShape, color: number): void {
  c.body.tint = color;
  c.top.tint = color;
  c.bottom.tint = color;
}

/** Linear blend of two 0xrrggbb colors, k from 0 (a) to 1 (b). */
export function mixColor(a: number, b: number, k: number): number {
  const ar = (a >> 16) & 255;
  const ag = (a >> 8) & 255;
  const ab = a & 255;
  const br = (b >> 16) & 255;
  const bg = (b >> 8) & 255;
  const bb = b & 255;
  const r = Math.round(ar + (br - ar) * k);
  const g = Math.round(ag + (bg - ag) * k);
  const bl = Math.round(ab + (bb - ab) * k);
  return (r << 16) | (g << 8) | bl;
}
